import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    SearchLg,
    Eye,
    Plus
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { Table, TableCard } from '@/components/application/table/table';
import { orderService } from '@/data/mock-orders';
import { accountService } from '@/data/mock-accounts';
import type { Order, OrderStatus } from '@/types/order';
import { useTranslation } from 'react-i18next';

export const ReceiverOrdersPage = () => {
    const { t } = useTranslation(['pages:orders', 'common']);
    const navigate = useNavigate();
    const [orders, setOrders] = useState<Order[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<OrderStatus | ''>('');

    const statusOptions = [
        { id: '', label: t('list.filters.allStatuses') },
        { id: 'Pending', label: t('status.pending', { ns: 'types' }) },
        { id: 'Accepted', label: t('status.accepted', { ns: 'types' }) },
        { id: 'Rejected', label: t('status.rejected', { ns: 'types' }) },
        { id: 'Modified', label: t('status.modified', { ns: 'types' }) },
        { id: 'Confirmed', label: t('status.confirmed', { ns: 'types' }) },
        { id: 'Cancelled', label: t('status.cancelled', { ns: 'types' }) }
    ];

    const getStatusColor = (status: OrderStatus) => {
        switch (status) {
            case 'Pending': return 'warning';
            case 'Accepted': return 'success';
            case 'Rejected': return 'error';
            case 'Modified': return 'warning';
            case 'Confirmed': return 'success';
            case 'Cancelled': return 'gray';
            default: return 'gray';
        }
    };

    const translateStatus = (status: OrderStatus) => {
        return t(`status.${status.toLowerCase()}`, { ns: 'types' });
    };

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            // Get current user ID from localStorage
            const accountId = localStorage.getItem('accountId') || '2';

            // Load orders for this receiver
            const receiverOrders = orderService.getByReceiverId(accountId);
            setOrders(receiverOrders);

            setIsLoading(false);
        };
        loadData();
    }, []);

    // Filter orders
    const filteredOrders = useMemo(() => {
        let filtered = [...orders];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(order =>
                order.notes?.toLowerCase().includes(term) ||
                order.packingList.requested?.toLowerCase().includes(term) ||
                order.contractualInfo?.contractNumber?.toLowerCase().includes(term)
            );
        }

        if (statusFilter) {
            filtered = filtered.filter(order => order.status === statusFilter);
        }

        return filtered;
    }, [orders, searchTerm, statusFilter]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('list.loading')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('receiver.title')}</h1>
                    <p className="text-tertiary mt-1">{t('receiver.subtitle')}</p>
                </div>
                <Button
                    onClick={() => navigate('/receiver/orders/new')}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                    iconLeading={Plus}
                >
                    {t('receiver.newOrder')}
                </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-col gap-4 sm:flex-row">
                <div className="flex-1">
                    <Input
                        placeholder={t('list.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />
                </div>
                <div className="w-full sm:w-64">
                    <Select
                        items={statusOptions}
                        selectedKey={statusFilter}
                        onSelectionChange={(key) => setStatusFilter(key as OrderStatus | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Orders Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={t('receiver.tableTitle')}
                    description={t('receiver.tableDescription', { count: filteredOrders.length })}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('receiver.headers.orderId')} isRowHeader />
                        <Table.Head label={t('receiver.headers.shipper')} />
                        <Table.Head label={t('receiver.headers.targetWeek')} />
                        <Table.Head label={t('receiver.headers.status')} />
                        <Table.Head label={t('receiver.headers.createdAt')} />
                        <Table.Head label={t('receiver.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {filteredOrders.length === 0 ? (
                            <Table.Row>
                                <Table.Cell colSpan={6} className="text-center py-8">
                                    <p className="text-tertiary">{t('list.noOrders')}</p>
                                </Table.Cell>
                            </Table.Row>
                        ) : (
                            filteredOrders.map((order) => {
                                const shipperAccount = accountService.getById(order.shipperId);

                                return (
                                    <Table.Row key={order.id}>
                                        <Table.Cell>
                                            <div className="font-medium text-primary">#{order.id}</div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="text-sm text-tertiary">
                                                {shipperAccount?.legalName || order.shipperId}
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="text-sm text-tertiary">
                                                Settimana {order.targetWeek}/{order.targetYear}
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Badge color={getStatusColor(order.status)} size="sm">
                                                {translateStatus(order.status)}
                                            </Badge>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="text-sm text-tertiary">
                                                {order.createdAt.toLocaleDateString('it-IT')}
                                            </span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Button
                                                onClick={() => navigate(`/receiver/orders/${order.id}`)}
                                                color="secondary"
                                                size="sm"
                                                iconLeading={Eye}
                                            >
                                                {t('buttons.view', { ns: 'common' })}
                                            </Button>
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })
                        )}
                    </Table.Body>
                </Table>
            </TableCard.Root>
        </div>
    );
};

